import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LOCATION_KIND_LABELS, LOCATION_KINDS, LOCATION_MAX_LEVEL, type InventoryLocation, type LocationKind } from '@kontor/shared';
import { api } from '../../api';
import { useSession } from '../../session';
import { InventoryTabs, errorText, useLocations } from './shared';
import './inventory.css';

interface Draft {
  id: string | null;
  parentId: string;
  code: string;
  name: string;
  kind: LocationKind;
  note: string;
}
const empty = (parentId = ''): Draft => ({ id: null, parentId, code: '', name: '', kind: parentId ? 'shelf' : 'warehouse', note: '' });

/** Lagerorte (KON-166): ein Baum mit höchstens drei Ebenen. Löschen geht nur, solange nichts am Ort hängt, sonst deaktiviert man ihn. */
export function LocationsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const locations = useLocations();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const mayWrite = can('inventory.write');
  const mayDelete = can('inventory.delete');
  const refresh = () => {
    for (const key of ['inventory-locations', 'inventory-stock', 'inventory-movements']) client.invalidateQueries({ queryKey: [key] });
  };
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { parentId: d.parentId || null, code: d.code.trim() || null, name: d.name.trim(), kind: d.kind, note: d.note.trim() || null };
      return d.id ? api('PATCH', `/inventory/locations/${d.id}`, body) : api('POST', '/inventory/locations', body);
    },
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: draft?.id ? 'Der Lagerort ist gespeichert.' : 'Der Lagerort ist angelegt.' });
      setDraft(null);
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const toggle = useMutation({
    mutationFn: (l: InventoryLocation) => api('PATCH', `/inventory/locations/${l.id}`, { active: !l.active }),
    onSuccess: refresh,
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Ändern hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: (l: InventoryLocation) => api('DELETE', `/inventory/locations/${l.id}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Der Lagerort ist gelöscht.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft) save.mutate(draft);
  };
  const edit = (l: InventoryLocation) => setDraft({ id: l.id, parentId: l.parentId ?? '', code: l.code ?? '', name: l.name, kind: l.kind, note: l.note ?? '' });

  return (
    <>
      <div className="page-head">
        <h1>Lagerorte</h1>
        {mayWrite && <button type="button" className="btn btn--primary" onClick={() => setDraft(empty())}>Neuer Lagerort</button>}
      </div>
      <InventoryTabs />
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {draft && (
        <form className="card inv-booking" onSubmit={submit} aria-label="Lagerort">
          <h2>{draft.id ? 'Lagerort bearbeiten' : 'Neuer Lagerort'}</h2>
          <div className="form-grid">
            <label className="field">
              <span>Name</span>
              <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={100} required autoFocus />
            </label>
            <label className="field">
              <span>Kürzel (optional)</span>
              <input className="input" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={20} />
            </label>
            <label className="field">
              <span>Art</span>
              <select className="input" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as LocationKind })}>
                {LOCATION_KINDS.map((k) => <option key={k} value={k}>{LOCATION_KIND_LABELS[k]}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Liegt in (optional)</span>
              <select className="input" value={draft.parentId} onChange={(e) => setDraft({ ...draft, parentId: e.target.value })}>
                <option value="">Oberste Ebene</option>
                {(locations.data ?? []).filter((l) => l.id !== draft.id && l.level < LOCATION_MAX_LEVEL).map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
              </select>
            </label>
            <label className="field field--wide">
              <span>Notiz (optional)</span>
              <input className="input" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} maxLength={500} />
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setDraft(null)}>Abbrechen</button>
          </div>
        </form>
      )}
      <div className="card table-card">
        <table className="table table--cards">
          <thead><tr><th>Lagerort</th><th>Art</th><th className="num">Artikel</th><th className="num">Inventar</th><th>Status</th><th /></tr></thead>
          <tbody>
            {locations.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {locations.data?.length === 0 && <tr><td colSpan={6} className="empty">Noch kein Lagerort. Legen Sie zuerst ein Lager an, zum Beispiel „Hauptlager“.</td></tr>}
            {locations.data?.map((l) => (
              <tr key={l.id} className={l.active ? '' : 'inv-reversed'}>
                <td data-label="Lagerort" style={{ paddingLeft: `${0.75 + (l.level - 1) * 1.25}rem` }}><strong>{l.name}</strong>{l.code && <span className="muted small"> · {l.code}</span>}{l.note && <div className="muted small">{l.note}</div>}</td>
                <td data-label="Art">{LOCATION_KIND_LABELS[l.kind]}</td>
                <td data-label="Artikel" className="num">{l.productCount}</td>
                <td data-label="Inventar" className="num">{l.assetCount}</td>
                <td data-label="Status">{l.active ? 'Aktiv' : 'Deaktiviert'}</td>
                <td className="doc-actions">
                  {mayWrite && l.level < LOCATION_MAX_LEVEL && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setDraft(empty(l.id))}>Unterort</button>}
                  {mayWrite && <button type="button" className="btn btn--secondary btn--sm" onClick={() => edit(l)}>Bearbeiten</button>}
                  {mayWrite && <button type="button" className="btn btn--secondary btn--sm" disabled={toggle.isPending} onClick={() => toggle.mutate(l)}>{l.active ? 'Deaktivieren' : 'Aktivieren'}</button>}
                  {mayDelete && <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`„${l.name}“ löschen?`) && remove.mutate(l)}>Löschen</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
